import { toast } from 'sonner'
import { refreshWebPushSubscription } from '@/lib/webPush'

const RELOAD_FLAG = 'xpillar-hero-sw-reloading'
const RELOADED_VERSION_KEY = 'xpillar-hero-sw-reloaded-version'
const CURRENT_ASSETS_KEY = 'xpillar-hero-current-build-assets'
const AUTO_REFRESH_DELAY_MS = 1800

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null
let checkingBuild = false

const clearRuntimeCaches = async () => {
    if ('caches' in window) {
        const keys = await caches.keys()
        await Promise.all(keys.filter(key => key.startsWith('xpillar-hero')).map(key => caches.delete(key)))
    }

    navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_RUNTIME_CACHES' })
}

const safeReloadForVersion = async (version?: string) => {
    if (sessionStorage.getItem(RELOAD_FLAG) === '1') return
    if (version && sessionStorage.getItem(RELOADED_VERSION_KEY) === version) return

    sessionStorage.setItem(RELOAD_FLAG, '1')
    if (version) sessionStorage.setItem(RELOADED_VERSION_KEY, version)
    await clearRuntimeCaches().catch(() => {})
    window.location.reload()
}

export const clearPwaReloadFlag = () => {
    sessionStorage.removeItem(RELOAD_FLAG)
}

const notifyUpdate = (worker: ServiceWorker) => {
    let activated = false
    const activate = () => {
        if (activated) return
        activated = true
        worker.postMessage({ type: 'SKIP_WAITING' })
    }

    toast('New version available', {
        duration: AUTO_REFRESH_DELAY_MS + 1200,
        action: {
            label: 'Refresh',
            onClick: activate,
        },
    })

    window.setTimeout(activate, AUTO_REFRESH_DELAY_MS)
}

const getDocumentAssets = (doc: Document) => {
    const scripts = Array.from(doc.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'))
        .map(el => new URL(el.src, window.location.origin).pathname)
    const styles = Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]'))
        .map(el => new URL(el.href, window.location.origin).pathname)
    return [...scripts, ...styles].filter(path => path.startsWith('/assets/')).sort()
}

const getCurrentDocumentAssets = () => getDocumentAssets(document)

const getAssetSignature = (assets: string[]) => assets.join('|')

const notifyBuildUpdate = () => {
    let refreshing = false
    const refresh = async () => {
        if (refreshing) return
        refreshing = true
        await safeReloadForVersion(`assets:${Date.now()}`)
    }

    toast('New version available', {
        duration: AUTO_REFRESH_DELAY_MS + 1200,
        action: {
            label: 'Refresh',
            onClick: refresh,
        },
    })

    window.setTimeout(refresh, AUTO_REFRESH_DELAY_MS)
}

export const checkForPwaBuildUpdate = async () => {
    if (checkingBuild || document.visibilityState === 'hidden') return
    checkingBuild = true

    try {
        const currentSignature = getAssetSignature(getCurrentDocumentAssets())
        if (currentSignature) {
            sessionStorage.setItem(CURRENT_ASSETS_KEY, currentSignature)
        }

        const response = await fetch(`/?__pwa_update_check=${Date.now()}`, {
            cache: 'no-store',
            headers: { Accept: 'text/html' },
        })
        if (!response.ok) return

        const html = await response.text()
        const nextDoc = new DOMParser().parseFromString(html, 'text/html')
        const nextSignature = getAssetSignature(getDocumentAssets(nextDoc))
        const baseline = currentSignature || sessionStorage.getItem(CURRENT_ASSETS_KEY) || ''

        if (baseline && nextSignature && baseline !== nextSignature) {
            notifyBuildUpdate()
        }
    } catch (err) {
        console.warn('[pwa] build update check failed', err)
    } finally {
        checkingBuild = false
    }
}

const handleWaitingWorker = (registration: ServiceWorkerRegistration) => {
    if (registration.waiting && navigator.serviceWorker.controller) {
        notifyUpdate(registration.waiting)
    }
}

export const registerPwaServiceWorker = () => {
    if (!('serviceWorker' in navigator)) return Promise.resolve(null)
    if (registrationPromise) return registrationPromise

    registrationPromise = navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
        .then(registration => {
            registration.update().catch(() => {})
            checkForPwaBuildUpdate().catch(() => {})
            handleWaitingWorker(registration)

            registration.addEventListener('updatefound', () => {
                const worker = registration.installing
                if (!worker) return

                worker.addEventListener('statechange', () => {
                    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
                        notifyUpdate(worker)
                    }
                })
            })

            window.addEventListener('focus', () => {
                registration.update().catch(() => {})
                checkForPwaBuildUpdate().catch(() => {})
            })
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    registration.update().catch(() => {})
                    checkForPwaBuildUpdate().catch(() => {})
                }
            })
            window.setInterval(() => {
                registration.update().catch(() => {})
                checkForPwaBuildUpdate().catch(() => {})
            }, 5 * 60 * 1000)

            return registration
        })
        .catch(err => {
            console.error('[pwa] service worker registration failed', err)
            return null
        })

    return registrationPromise
}

export const subscribePwaServiceWorkerEvents = () => {
    if (!('serviceWorker' in navigator)) return () => {}
    const hadControllerAtStart = Boolean(navigator.serviceWorker.controller)

    const onMessage = (event: MessageEvent) => {
        if (event.data?.type === 'WEB_PUSH_RESUBSCRIBE') {
            refreshWebPushSubscription().catch(() => {})
        }

        if (event.data?.type === 'SW_ACTIVATED') {
            if (hadControllerAtStart) safeReloadForVersion(event.data.version)
        }
    }

    const onControllerChange = () => {
        if (hadControllerAtStart) safeReloadForVersion()
    }

    navigator.serviceWorker.addEventListener('message', onMessage)
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)

    return () => {
        navigator.serviceWorker.removeEventListener('message', onMessage)
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
    }
}
